/**
 * Utility functions for Laporan Rekap 1 Data Pemantauan Jentik.
 */

/**
 * Convert a value to a non-negative integer count.
 * Returns 0 if value is null, undefined, NaN, or negative.
 *
 * @param {any} val
 * @returns {number}
 */
export const toCount = (val) => {
  const num = parseInt(val, 10);
  return Number.isNaN(num) || num < 0 ? 0 : num;
};

/**
 * Format numerator / denominator as percentage with 1 decimal place.
 * Returns "0.0%" if denominator is 0 (or null/undefined).
 *
 * @param {any} numerator
 * @param {any} denominator
 * @returns {string}
 */
export const formatPercent = (numerator, denominator) => {
  const den = toCount(denominator);
  if (den === 0) {
    return "0.0%";
  }
  const num = toCount(numerator);
  return `${((num / den) * 100).toFixed(1)}%`;
};

/**
 * Calculate the row percentage value (0 to 100).
 * Returns 0 if denominator is 0 (or null/undefined).
 *
 * @param {any} num
 * @param {any} den
 * @returns {number}
 */
export const rowPercentValue = (num, den) => {
  const d = toCount(den);
  if (d === 0) return 0;
  return (toCount(num) / d) * 100;
};

/**
 * Calculate the average percentage of rows for a given numerator and denominator key.
 * Returns "0.0%" if rows is empty.
 *
 * @param {Array<Object>} rows
 * @param {string} numKey
 * @param {string} denKey
 * @returns {string}
 */
export const averagePercent = (rows = [], numKey, denKey) => {
  if (!rows.length) return "0.0%";
  const sum = rows.reduce(
    (acc, r) => acc + rowPercentValue(r?.[numKey], r?.[denKey]),
    0
  );
  return `${(sum / rows.length).toFixed(1)}%`;
};

/**
 * Sum metric columns for an array of rows and calculate overall ratios.
 *
 * @param {Array<Object>} rows
 * @returns {Object}
 */
export const sumRows = (rows = []) => {
  const totals = {
    jumlah_tp_disurvei: 0,
    status_masih_ada: 0,
    status_tidak_ada: 0,
    tidak_ada_karena_intervensi: 0,
    tidak_ada_bukan_karena_intervensi: 0,
    positif_jentik: 0,
    negatif_jentik: 0,
    sudah_intervensi: 0,
    belum_intervensi: 0,
  };

  rows.forEach((row) => {
    totals.jumlah_tp_disurvei += toCount(row?.jumlah_tp_disurvei);
    totals.status_masih_ada += toCount(row?.status_masih_ada);
    totals.status_tidak_ada += toCount(row?.status_tidak_ada);
    totals.tidak_ada_karena_intervensi += toCount(
      row?.tidak_ada_karena_intervensi
    );
    totals.tidak_ada_bukan_karena_intervensi += toCount(
      row?.tidak_ada_bukan_karena_intervensi
    );
    totals.positif_jentik += toCount(row?.positif_jentik);
    totals.negatif_jentik += toCount(row?.negatif_jentik);
    totals.sudah_intervensi += toCount(row?.sudah_intervensi);
    totals.belum_intervensi += toCount(row?.belum_intervensi);
  });

  return {
    ...totals,
    indeks_habitat: formatPercent(
      totals.positif_jentik,
      totals.status_masih_ada
    ),
    cakupan_intervensi: formatPercent(
      totals.sudah_intervensi,
      totals.positif_jentik
    ),
  };
};
